import { PortableTextBlock } from '@portabletext/react';
import clsx from 'clsx';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useContext, useState } from 'react';
import { A11y, EffectCoverflow, Keyboard, Navigation } from 'swiper/modules';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import { twMerge } from 'tailwind-merge';

import { Container, Section } from 'components/GridV2';

import { IQueryParams, ISanityImgWithAltText, ITrackEventProps } from 'typescriptDefs';

import { useIsMobileSize } from 'helpers';

import CustomPortableText from './CustomPortableText';
import { GetKOHOBtn } from './GetKOHOBtn';
import { LocaleContext } from './Layout';
import { NavigationButtons } from './SwiperCarousel';
import { H1, H2, H4, P } from './Typography';

type CarouselCard = {
  heading: string;
  description: string | PortableTextBlock | PortableTextBlock[];
  image: ISanityImgWithAltText;
};

type CarouselVariant = 'primaryLight' | 'primaryXLight';

type Props = {
  className?: string;
  cta?: {
    text: string;
    tracking: {
      queryParams: IQueryParams;
      trackEventProps: ITrackEventProps;
    };
  };
  heading: string;
  headingSize?: 'h1' | 'h2';
  subheading?: string;
  carouselData: CarouselCard[];
  variant?: CarouselVariant;
};

const CoverflowCarousel = ({
  className,
  cta,
  heading,
  headingSize = 'h1',
  subheading,
  carouselData,
  variant = 'primaryXLight',
}: Props) => {
  const isMobile = useIsMobileSize();

  const [activeSlide, setActiveSlide] = useState(1);

  return (
    <Section
      className={twMerge('bg-primary-50 py-64 md:py-96', variant === 'primaryLight' && 'bg-primary-75', className)}
    >
      <Container className="px-32 xs:px-0">
        <div className="flex justify-center">
          <div className="text-center">
            {subheading || headingSize === 'h2' ? (
              <H2 className={twMerge('mx-auto text-primary-400 sm:max-w-800', !subheading && 'mb-64')}>{heading}</H2>
            ) : (
              <H1 size="2xl" as="h2" className="mx-auto mb-64 text-primary-400">
                {heading}
              </H1>
            )}
            {subheading && (
              <p className="mx-auto mb-32 mt-24 text-md font-normal leading-normal sm:max-w-800 md:mb-48 md:text-lg">
                {subheading}
              </p>
            )}
            {!!cta && (
              <GetKOHOBtn
                buttonClassName="mb-48 md:mb-64"
                buttonLocation="Coverflow Carousel"
                queryParams={cta.tracking.queryParams}
                trackEventProps={cta.tracking.trackEventProps}
              >
                {cta.text}
              </GetKOHOBtn>
            )}
          </div>
        </div>
      </Container>
      {isMobile ? (
        <MobileSwiper
          carouselData={carouselData}
          onSlideChange={(swiper) => setActiveSlide(swiper.activeIndex)}
          variant={variant}
        />
      ) : (
        <DesktopSwiper
          activeSlide={activeSlide}
          setActiveSlide={setActiveSlide}
          carouselData={carouselData}
          onSlideChange={(swiper) => setActiveSlide(swiper.activeIndex)}
          variant={variant}
        />
      )}
    </Section>
  );
};

export default CoverflowCarousel;

const Description = ({ content }: { content: string | PortableTextBlock | PortableTextBlock[] }) => {
  return typeof content === 'string' ? <P>{content}</P> : <CustomPortableText content={content} />;
};

const DesktopSwiper = ({
  activeSlide,
  carouselData,
  onSlideChange,
  setActiveSlide,
  variant,
}: {
  activeSlide: number;
  carouselData: CarouselCard[];
  onSlideChange: SwiperProps['onSlideChange'];
  setActiveSlide: (index: number) => void;
  variant: CarouselVariant;
}) => {
  const locale = useContext(LocaleContext);
  return (
    <Swiper
      className="[&_.swiper-wrapper]:items-center"
      slidesPerView="auto"
      spaceBetween={30}
      speed={1000}
      coverflowEffect={{
        rotate: 0,
        stretch: 0,
        depth: 0,
        modifier: 0,
        slideShadows: false,
      }}
      centeredSlides
      initialSlide={1}
      effect="coverflow"
      grabCursor
      modules={[EffectCoverflow, A11y, Navigation, Keyboard]}
      a11y={{
        prevSlideMessage: {
          en: 'Previous slide',
          fr: 'Diapositive précédente',
        }[locale],
        nextSlideMessage: { en: 'Next slide', fr: 'Diapositive suivante' }[locale],
      }}
      keyboard
      navigation={{ enabled: true, nextEl: `.why-use-virtual-card-next`, prevEl: `.why-use-virtual-card-prev` }}
      onSlideChange={onSlideChange}
      onClick={(swiper) => {
        setActiveSlide(swiper.clickedIndex);
        swiper.slideTo(swiper.clickedIndex);
      }}
    >
      {carouselData.map((slide, i) => (
        <SwiperSlide key={i} className={twMerge(clsx('!w-645'))}>
          <div className={twMerge('rounded-12 bg-primary-75 p-32', variant === 'primaryLight' && 'bg-primary-50')}>
            <GatsbyImage
              className={twMerge(
                clsx('h-186 rounded-8', activeSlide === i && 'sm:h-403', activeSlide !== i && 'sm:h-336'),
              )}
              alt={slide.image.altText}
              image={slide.image.img.asset.gatsbyImageData}
            />
            <div className="pt-32">
              <H4
                headingStyle
                className={twMerge(
                  clsx('text-primary-400', activeSlide === i && 'pb-8', activeSlide !== i && 'md:text-3xl'),
                )}
              >
                {slide.heading}
              </H4>
              <div className="w-4/5">{activeSlide === i && <Description content={slide.description} />}</div>
            </div>
          </div>
        </SwiperSlide>
      ))}
      <NavButtons variant={variant} />
    </Swiper>
  );
};

const MobileSwiper = ({
  carouselData,
  onSlideChange,
  variant,
}: {
  carouselData: CarouselCard[];
  onSlideChange: SwiperProps['onSlideChange'];
  variant: CarouselVariant;
}) => {
  const locale = useContext(LocaleContext);
  return (
    <Swiper
      className="[&_.swiper-wrapper]:items-center"
      autoplay={{ delay: 5000 }}
      slidesPerView="auto"
      spaceBetween={16}
      slidesOffsetBefore={32}
      slidesOffsetAfter={32}
      grabCursor
      modules={[A11y, Navigation, Keyboard]}
      a11y={{
        prevSlideMessage: {
          en: 'Previous slide',
          fr: 'Diapositive précédente',
        }[locale],
        nextSlideMessage: { en: 'Next slide', fr: 'Diapositive suivante' }[locale],
      }}
      keyboard
      navigation={{ enabled: true, nextEl: `.why-use-virtual-card-next`, prevEl: `.why-use-virtual-card-prev` }}
      onSlideChange={onSlideChange}
    >
      {carouselData.map((slide, i) => (
        <SwiperSlide key={i} className="!h-445 !w-295 overflow-hidden !rounded-12">
          <div
            className={twMerge('h-full rounded-12 bg-primary-75 p-32', variant === 'primaryLight' && 'bg-primary-50')}
          >
            <GatsbyImage
              className={twMerge(clsx('h-186 rounded-8'))}
              alt={slide.image.altText}
              image={slide.image.img.asset.gatsbyImageData}
            />
            <div className="pt-32">
              <H4 headingStyle className={twMerge('pb-8 text-primary-400')}>
                {slide.heading}
              </H4>
              <Description content={slide.description} />
            </div>
          </div>
        </SwiperSlide>
      ))}
      <NavButtons variant={variant} />
    </Swiper>
  );
};

const NavButtons = ({ variant }: { variant: CarouselVariant }) => {
  return (
    <Container className="px-32 pt-32 xs:px-0">
      <div className="mt-48 flex items-center justify-center">
        <NavigationButtons
          className={twMerge('pb-4', variant === 'primaryLight' && '[&_button:disabled]:bg-grey-50')}
          nextNavEl="why-use-virtual-card-next"
          prevNavEl="why-use-virtual-card-prev"
          buttonVariant="primary"
        />
      </div>
    </Container>
  );
};
